import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { StripeCustomerData } from 'src/app/shared/models/stripe/stripe-customer-data';
import { StripeSearchOptions } from 'src/app/shared/models/stripe/stripe-search-options';
import { StripSearchResult } from 'src/app/shared/models/stripe/stripe-search-result';
import { GtStripeService } from 'src/app/shared/services/gt-stripe.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { SimulatorService } from '../../services/simulator.service';
import { MatDialog } from '@angular/material/dialog';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { AccountService } from 'src/app/account/account.service';
import { MessageModalComponent } from 'src/app/shared/modals/message-modal/message-modal.component';
import { Router } from '@angular/router';

@Component({
  selector: 'simbook-plans',
  templateUrl: './plans.component.html',
  styleUrl: './plans.component.scss',
  standalone: false
})
export class PlansComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() isActive: boolean = false;

  isSkeletonLoading: boolean;
  products: any[] = [];
  currentProduct: any;
  private stripeCustomer: any;
  private simulatorCount: number = 0;
  private option: StripeSearchOptions = new StripeSearchOptions();

  constructor(sessionService: SessionService,
    stringHelpers: StringHelpers,
    private stripeService: GtStripeService,
    private simulatorService: SimulatorService,
    private accountService: AccountService,
    public dialog: MatDialog,
    private router: Router
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    if (this.isActive) {
      this.getProducts();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive'] && changes['isActive'].currentValue === true) {
      this.isSkeletonLoading = true;
      this.getProducts();
    }
  }

  getProducts() {
    this.isLoading = true;
    this.stripeService.getProducts({ platform: 'simbook' }).subscribe({
      next: (products) => {
        this.products = products.data;
        this.getCustomersCurrentProduct();
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.getUserDetails();
      }
    });
  }

  getCustomersCurrentProduct() {
    this.stripeService.getCustomersCurrentProduct({ stripeAccountId: this.getUserFromSession().stripeAccountId }).subscribe({
      next: (product) => {
        this.currentProduct = product;
        this.getCustomerSubscription();
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  getCustomerSubscription() {
    this.option.query = `metadata['GolfTechId']:'${this.sessionService.getUserId()}'`;
    this.stripeService.getCustomer(this.option).subscribe({
      next: (result: StripSearchResult<StripeCustomerData>) => {
        this.stripeCustomer = result.data[0];
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.getSimulatorCount();
      }
    })
  }

  getSimulatorCount() {
    this.simulatorService.getAllByOrganization({ organizationId: this.getUserFromSession().organizationId }).subscribe({
      next: (simulators) => {
        this.simulatorCount = simulators.results.filter(s => s.isAvailable === true).length;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isLoading = false;
        this.isSkeletonLoading = false;
      }
    });
  }

  getUserDetails() {
    this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
      next: (result) => {
        this.sessionService.setUser(result);
      }
    });
  }

  createOrUpdateSubscription(product: any) {
    if (this.currentProduct?.id !== null) {
      let dto = {
        subscriptionId: this.stripeCustomer.subscriptions.data[0].id,
        subscriptionItemId: this.stripeCustomer.subscriptions.data[0].items.data[0].id,
        priceId: product.default_price.id,
        quantity: this.simulatorCount == 0 ? 1 : this.simulatorCount
      };

      let productPermissions = JSON.parse(product.metadata['permissions']);
      let subscriptionMessage = this.buildSubscriptionUpdateMessage(product, productPermissions.simulatorCount);

      if (subscriptionMessage.canUpdate) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
          height: '200px',
          width: 'auto'
        });

        dialogRef.componentInstance.title = 'Confirm Subscription';
        dialogRef.componentInstance.body = subscriptionMessage.body;
        dialogRef.componentInstance.submitText = 'Confirm';
        dialogRef.componentInstance.otherText = 'Cancel';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe(result => {
          if (result.toLocaleLowerCase() == 'confirm') {
            this.isLoading = true;
            this.stripeService.updateSubscription(dto).subscribe({
              next: (subscription) => {
                this.alertModal('Subscription Updated', "Your subscription has been updated successfully. This page will refresh after you close this dialog.", 'OK', null, false, async () => {
                  this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
                    next: (result) => {
                      this.sessionService.setUser(result);
                      window.location.reload();
                    }
                  });
                });
              },
              error: (error) => {
                this.alertModal('Error', "An error occurred while updating your subscription. Please try again. If the problem persists, please contact support.", 'OK', null, true, null);
                console.error(error);
              },
              complete: () => {
                this.isLoading = false;
              }
            });
          }
        });
      } else {
        this.alertModal('Cannot Update Subscription', subscriptionMessage.body, 'OK', null, false, null);
        return;
      }

    } else {

      let dto = {
        customerId: this.stripeCustomer.id,
        priceId: product.default_price.id,
        quantity: this.simulatorCount == 0 ? 1 : this.simulatorCount
      };

      const dialogRef = this.dialog.open(AlertModalComponent, {
        height: '200px',
        width: 'auto'
      });

      dialogRef.componentInstance.title = 'Confirm Subscription';
      dialogRef.componentInstance.body = 'Are you sure you want to create your subscription? You will be charged $' + product.default_price.unit_amount / 100 + ' per month for ' + this.simulatorCount + ' simulator(s).';
      dialogRef.componentInstance.submitText = 'Confirm';
      dialogRef.componentInstance.otherText = 'Cancel';
      dialogRef.componentInstance.isOtherTextDisabled = false;

      dialogRef.afterClosed().subscribe(result => {
        if (result.toLocaleLowerCase() == 'confirm') {
          this.isLoading = true;
          this.stripeService.createSubscription(dto).subscribe({
            next: (subscription) => {
              this.alertModal('Subscription Created', "Your subscription has been created successfully. This page will refresh after you close this dialog.", 'OK', null, false, () => {
                this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
                  next: (result) => {
                    this.sessionService.setUser(result);
                    this.router.navigateByUrl('/simulator-management', { skipLocationChange: false });
                  }
                });
              });
            },
            error: (error) => {
              this.isLoading = false;
              this.alertModal('Error', "An error occurred while updating your subscription. Please try again. If the problem persists, please contact support.", 'OK', null, false, null);
              console.error(error);
            },
            complete: () => {
              this.isLoading = false;
            }
          });
        }
      });
    }
  }

  private buildSubscriptionUpdateMessage(product: any, productPermissions: any): { body: string, canUpdate: boolean } {
    const monthlyPrice = product.default_price.unit_amount / 100;
    const currentSimulatorCount = this.simulatorCount;
    const newSimulatorLimit = productPermissions.simulatorCount;

    let message = 'Are you sure you want to update your subscription? ';
    let canUpdate = true;

    if (newSimulatorLimit === 0) {
      message += `You will be charged $${monthlyPrice} per month per simulator for unlimited simulators.`;
    } else {
      message += `You will be charged $${monthlyPrice} per month per simulator for up to ${newSimulatorLimit} simulator(s).`;

      if (currentSimulatorCount > newSimulatorLimit) {
        message = `You currently have ${currentSimulatorCount} active simulators, but this plan only supports ${newSimulatorLimit}. You must mark ${currentSimulatorCount - newSimulatorLimit} simulator(s) as inactive before proceeding with the subscription update.`;
        canUpdate = false;
      }
    }

    return { body: message, canUpdate };
  }

  alertModal(title: string, body: string, okayButtonText: string, otherButtonText: string, isOtherButtonVisible: boolean, callback: () => void) {
    const dialogRef = this.dialog.open(MessageModalComponent, {
      height: 'auto',
      width: 'auto'
    });

    dialogRef.componentInstance.title = title;
    dialogRef.componentInstance.body = body;
    dialogRef.componentInstance.okayButtonText = okayButtonText;
    dialogRef.componentInstance.otherButtonText = otherButtonText;
    dialogRef.componentInstance.isOtherButtonVisible = isOtherButtonVisible;

    dialogRef.afterClosed().subscribe(result => {
      if (result.toLocaleLowerCase() == okayButtonText.toLocaleLowerCase()) {
        callback();
      }
    });
  }
}